import { useId, type ReactNode } from "react";

/**
 * apps/web :: components :: ChannelToggle
 *
 * Reusable "channel row" used by NotificationPreferences — a boolean toggle
 * plus an optional contact-field input and optional helper/warning slots.
 *
 * Intentionally dumb: no state, no network. The parent owns the value,
 * dispatches changes, and handles validation. Styling mirrors the
 * marketing-preset look already used in LoginPage / TopNav (brand-primary
 * borders, white cards, accent-amber highlight on validation errors).
 */
export interface ChannelToggleProps {
  /** Stable identifier used to key per-field `data-testid` hooks. */
  channelId: string;
  /** Channel label, e.g. "Email". Localised by the caller. */
  label: string;
  /** Currently-enabled flag. */
  enabled: boolean;
  /** Receives the new boolean when the toggle flips. */
  onToggle: (next: boolean) => void;
  /**
   * If true the toggle is rendered as a disabled control with a tooltip-ish
   * helper message. Used for Email (account-security requirement).
   */
  forcedOn?: boolean;
  /** Short explanatory copy rendered under the label. */
  helperText?: string;
  /** Optional badge/slot to the right of the label (e.g. "required"). */
  badge?: ReactNode;
  /** Optional contact-field block rendered inside the row when enabled. */
  contactField?: ContactFieldProps;
  /** Inline validation error for this row (red text, aria-described). */
  errorText?: string;
}

/** Optional contact-field details (phone / chat ID / etc.). */
export interface ContactFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  inputMode?: "text" | "tel" | "numeric";
  helperText?: string;
  /** Marks this field required — drives `aria-required` + visual hint. */
  required?: boolean;
}

export default function ChannelToggle({
  channelId,
  label,
  enabled,
  onToggle,
  forcedOn = false,
  helperText,
  badge,
  contactField,
  errorText,
}: ChannelToggleProps) {
  const toggleId = useId();
  const helperId = useId();
  const errorId = useId();

  const describedBy = [helperText ? helperId : null, errorText ? errorId : null]
    .filter(Boolean)
    .join(" ") || undefined;

  return (
    <div
      data-testid={`channel-row-${channelId}`}
      className={`rounded-md border p-md ${
        errorText
          ? "border-danger/60 bg-danger/5"
          : "border-brand-primary/10 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-md">
        <div className="flex-1">
          <label
            htmlFor={toggleId}
            className="flex items-center gap-sm text-sm font-semibold text-brand-primary"
          >
            <span>{label}</span>
            {badge}
          </label>
          {helperText && (
            <p id={helperId} className="mt-xs text-xs text-neutral-600">
              {helperText}
            </p>
          )}
        </div>
        <div className="shrink-0">
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              id={toggleId}
              type="checkbox"
              role="switch"
              checked={enabled}
              disabled={forcedOn}
              aria-describedby={describedBy}
              onChange={(ev) => onToggle(ev.target.checked)}
              data-testid={`channel-toggle-${channelId}`}
              className="peer sr-only"
            />
            <span
              aria-hidden="true"
              className={`h-6 w-11 rounded-full bg-neutral-300 transition-colors after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-brand-primary peer-checked:after:translate-x-5 peer-disabled:cursor-not-allowed peer-disabled:opacity-70`}
            />
          </label>
        </div>
      </div>

      {contactField && enabled && (
        <ContactField
          channelId={channelId}
          {...contactField}
        />
      )}

      {errorText && (
        <p
          id={errorId}
          role="alert"
          className="mt-sm text-xs font-medium text-danger"
          data-testid={`channel-error-${channelId}`}
        >
          {errorText}
        </p>
      )}
    </div>
  );
}

function ContactField({
  channelId,
  label,
  value,
  onChange,
  placeholder,
  inputMode = "text",
  helperText,
  required,
}: ContactFieldProps & { channelId: string }) {
  const inputId = useId();
  const helpId = useId();
  return (
    <div className="mt-md">
      <label
        htmlFor={inputId}
        className="block text-xs font-medium text-brand-primary"
      >
        {label}
        {required && <span className="ml-xs text-danger">*</span>}
      </label>
      <input
        id={inputId}
        type="text"
        inputMode={inputMode}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        placeholder={placeholder}
        aria-required={required || undefined}
        aria-describedby={helperText ? helpId : undefined}
        data-testid={`channel-contact-${channelId}`}
        className="mt-xs block w-full rounded-md border border-brand-primary/20 bg-white px-sm py-xs text-sm text-brand-primary placeholder:text-neutral-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      />
      {helperText && (
        <p id={helpId} className="mt-xs text-xs text-neutral-600">
          {helperText}
        </p>
      )}
    </div>
  );
}
